import React, { useEffect, useState } from "react";
import { format, nextDay } from "date-fns";

import {
  Button,
  List,
  Spin,
  message,
  Divider,
  Popconfirm,
  notification,
  Tag,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  WhatsAppOutlined,
  TeamOutlined,
  BookOutlined,
  DollarOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setAllCources } from "../../../Reducers/allCourcesReducer";
import { setAllSessons } from "../../../Reducers/allSessonsReducer";

// API
import {
  attendToSession,
  deleteSession,
  getAllCoursesByRole,
  getDashboardHomeCources,
  getNotesByUser,
  getSessions,
  getAllUsers,
  getAllPayments,
  getAllUsersOfACourse,
  getCoordinatorStats,
  getCoordinatorChartData,
} from "../../../Constants/Api/functions/function";

// Quill
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";

import { Link } from "react-router-dom";

import "./style.scss";
import AddSession from "./AddSession";
import EditSession from "./EditSession";

import { DEFAULT_USER_PROFILE_IMAGE, IMAGE_BASE_URL } from "../../../Constants/Api/azure/azure";

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const uploadsIdx = url.indexOf('uploads/');
  if (uploadsIdx !== -1) {
    return `${IMAGE_BASE_URL}/${url.substring(uploadsIdx)}`;
  }
  return url;
};

const PromoCountdown = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!endDate) return;
    
    const calculateTimeLeft = () => {
      const difference = +new Date(endDate) - +new Date();
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        return `Ends in ${days}d ${hours}h ${minutes}m`;
      }
      return 'Promotion Ended';
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (!timeLeft || timeLeft === 'Promotion Ended') return null;

  return (
    <div style={{
      display: 'inline-block',
      padding: '2px 8px',
      background: '#fff2f0',
      border: '1px solid #ffccc7',
      borderRadius: '4px',
      color: '#ff4d4f',
      fontSize: '11px',
      fontWeight: 'bold',
      marginTop: '4px'
    }}>
      <ClockCircleOutlined style={{ marginRight: '4px' }} />
      {timeLeft}
    </div>
  );
};

// Interactive SVG chart component for coordinator dashboard
const InteractiveChart = ({ data, dataKey, title, strokeColor, gradientId, gradientColors, isCurrency }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="custom-chart-card empty" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '280px' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>{title}</h3>
        <p style={{ margin: '12px 0 0 0', color: '#9ca3af', fontSize: '0.9rem' }}>No data available for the last 6 months.</p>
      </div>
    );
  }

  const width = 600;
  const height = 360;
  const padding = { top: 30, right: 30, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxVal = Math.max(...data.map(d => Number(d[dataKey]) || 0), 10);

  const points = data.map((d, index) => {
    const x = padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
    const val = Number(d[dataKey]) || 0;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    return { x, yCoord, value: val, label: d.month };
  });

  let pathD = "";
  let areaD = "";
  if (points.length > 0) {
    pathD = `M ${points[0].x} ${points[0].yCoord}`;
    for (let i = 1; i < points.length; i++) { pathD += ` L ${points[i].x} ${points[i].yCoord}`; }
    areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;
  }

  const gridLines = [];
  for (let i = 0; i <= 4; i++) {
    const val = (maxVal / 4) * i;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    gridLines.push({ val, yCoord });
  }

  const formatYValue = (val) => {
    if (isCurrency) {
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
      return val;
    }
    return Math.round(val);
  };

  const formatTooltipValue = (val) => {
    if (isCurrency) return `LKR ${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `${val} Enrolled`;
  };

  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>{title}</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>Last 6 Months Trend</p>
        </div>
        {hoveredIndex !== null ? (
          <div style={{ color: strokeColor, textAlign: 'right' }}>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{points[hoveredIndex].label}: </span>
            <span style={{ fontSize: '1rem', fontWeight: '700' }}>{formatTooltipValue(points[hoveredIndex].value)}</span>
          </div>
        ) : (
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Total: </span>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
              {isCurrency
                ? `LKR ${data.reduce((sum, d) => sum + (Number(d[dataKey]) || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                : `${data.reduce((sum, d) => sum + (Number(d[dataKey]) || 0), 0)} Total`}
            </span>
          </div>
        )}
      </div>
      <div style={{ position: 'relative', width: '100%', height: '280px' }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gradientColors[0]} stopOpacity="0.4" />
              <stop offset="100%" stopColor={gradientColors[1]} stopOpacity="0.0" />
            </linearGradient>
          </defs>
          {gridLines.map((line, idx) => (
            <g key={idx}>
              <line x1={padding.left} y1={line.yCoord} x2={width - padding.right} y2={line.yCoord} stroke="#f3f4f6" strokeDasharray="4 4" />
              <text x={padding.left - 10} y={line.yCoord + 4} textAnchor="end" fill="#9ca3af" fontSize="10" fontWeight="500">{formatYValue(line.val)}</text>
            </g>
          ))}
          <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} stroke="#e5e7eb" />
          {areaD && <path d={areaD} fill={`url(#${gradientId})`} />}
          {pathD && <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
          {points.map((p, idx) => (
            <text key={idx} x={p.x} y={padding.top + chartHeight + 20} textAnchor="middle" fill="#9ca3af" fontSize="10" fontWeight="600">{p.label}</text>
          ))}
          {points.map((p, idx) => (
            <circle key={idx} cx={p.x} cy={p.yCoord} r={hoveredIndex === idx ? 6 : 4} fill={strokeColor} stroke="#ffffff" strokeWidth="2" style={{ transition: 'all 0.15s ease-out' }} />
          ))}
          {points.map((p, idx) => {
            const colWidth = chartWidth / (points.length - 1 || 1);
            return (
              <rect key={idx} x={p.x - colWidth / 2} y={padding.top} width={colWidth} height={chartHeight} fill="transparent" style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIndex(idx)} onMouseLeave={() => setHoveredIndex(null)} />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const Home = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [api, contextHolderNoti] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);
  const [isAddSessionOpen, setIsAddSessionOpen] = useState(false);
  const [isEditSessionOpen, setIsEditSessionOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState();
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const [cources, setCources] = useState();
  const [homeCourses, setHomeCourses] = useState();
  const filteredHomeCourses = React.useMemo(() => {
    if (!homeCourses) return [];
    if (userProfile?.role === "coordinator") {
      return homeCourses.filter((c) => c.coordinatorId && String(c.coordinatorId) === String(userProfile.id));
    }
    return homeCourses;
  }, [homeCourses, userProfile]);
  const [notes, setNotes] = useState();
  const [sessions, setSessions] = useState();

  const isStaffSessionManager = ["superadmin", "admin", "coordinator"].includes(userProfile?.role);

  const publishedSessions = React.useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    return sessions.filter((s) => s.isPublished === 1 || s.isPublished === true || s.isPublished === "true");
  }, [sessions]);

  const unpublishedSessions = React.useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    return sessions.filter((s) => !s.isPublished || s.isPublished === 0 || s.isPublished === "false");
  }, [sessions]);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    hadleGetCources();
    handleGetSessions();
    handleGetNote();
    handleGetHomeCourses();
  }, []);

  useEffect(() => {
    if (userProfile?.role === "coordinator") {
      handleGetCoordinatorStats();
      handleGetCoordinatorChartData();
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile && (userProfile.role === "superadmin" || userProfile.role === "admin")) {
      const loadAdminStats = async () => {
        try {
          const centerFilter = userProfile.role === "admin" ? (userProfile.centerId || 2) : null;
          const [usersList, coursesList, paymentsList] = await Promise.all([
            getAllUsers(centerFilter),
            getAllCoursesByRole(userProfile.role, userProfile.id),
            getAllPayments()
          ]);

          const studentsCount = usersList?.filter((u) => u.role === "student")?.length || 0;
          const instructorsCount = usersList?.filter((u) => u.role === "instructor")?.length || 0;
          const activeCoursesCount = coursesList?.filter((c) => c.isPublished)?.length || 0;
          const totalRevenue = paymentsList
            ?.filter((p) => {
              const status = p.paymentStatus?.toLowerCase();
              return status === "success" || status === "paid" || status === "completed";
            })
            ?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;

          setStats({
            studentsCount,
            activeCoursesCount,
            instructorsCount,
            totalRevenue
          });
        } catch (error) {
          console.error("Failed to load admin stats:", error);
        }
      };
      loadAdminStats();
    }
  }, [userProfile]);

  const handleGetCoordinatorStats = async () => {
    if (!userProfile?.id) return;
    setStatsLoading(true);
    try {
      const response = await getCoordinatorStats(userProfile.id);
      if (response && response.success) {
        setStats(response);
      }
    } catch (error) {
      console.error("handleGetCoordinatorStats", error);
    }
    setStatsLoading(false);
  };

  const handleGetCoordinatorChartData = async () => {
    if (!userProfile?.id) return;
    setChartLoading(true);
    try {
      const response = await getCoordinatorChartData(userProfile.id);
      if (response && response.success) {
        setChartData(response.chartData || []);
      }
    } catch (error) {
      console.error("handleGetCoordinatorChartData", error);
    }
    setChartLoading(false);
  };

  useEffect(() => {
    if (userProfile && userProfile.role === "instructor") {
      const loadInstructorStats = async () => {
        try {
          const coursesList = await getAllCoursesByRole(userProfile.role, userProfile.id);
          const assignedCourses = coursesList?.filter(c => String(c.instructorId) === String(userProfile.id)) || [];
          const sessionsList = await getSessions(userProfile.role, userProfile.id);
          const instructorSessionsCount = sessionsList?.filter(s => assignedCourses.some(c => c.id === s.courseId))?.length || sessionsList?.length || 0;

          // Fetch enrolled students for assigned courses in parallel
          let totalStudentsCount = 0;
          try {
            const studentPromises = assignedCourses.map(c => getAllUsersOfACourse(c.id));
            const studentsResults = await Promise.all(studentPromises);
            const uniqueStudents = new Set();
            studentsResults.forEach(list => {
              if (Array.isArray(list)) {
                list.forEach(student => {
                  if (student && student.id) {
                    uniqueStudents.add(student.id);
                  }
                });
              }
            });
            totalStudentsCount = uniqueStudents.size;
          } catch (studentErr) {
            console.error("Failed to fetch student count:", studentErr);
          }

          // Count all course materials (lessons/content)
          const totalMaterialsCount = assignedCourses.reduce((sum, c) => sum + (c.courseContents?.length || 0), 0);

          setStats({
            instructorCoursesCount: assignedCourses.length,
            instructorSessionsCount,
            instructorStudentsCount: totalStudentsCount,
            instructorMaterialsCount: totalMaterialsCount,
          });
        } catch (error) {
          console.error("Failed to load instructor stats:", error);
        }
      };
      loadInstructorStats();
    }
  }, [userProfile]);

  const handleGetHomeCourses = async () => {
    try {
      const response = await getDashboardHomeCources(userProfile?.role, userProfile?.id);
      if (response && Array.isArray(response)) {
        // Create new array reference to ensure React detects changes
        setHomeCourses([...response]);
      } else {
        setHomeCourses([]);
      }
    } catch (error) {
      console.error("handleGetHomeCourses", error);
      setHomeCourses([]);
    }
  };

  const hadleGetCources = async () => {
    if (userProfile && (userProfile.role === "superadmin" || userProfile.role === "admin" || userProfile.role === "coordinator")) {
      try {
        const response = await getAllCoursesByRole(userProfile.role, userProfile.id);
        if (response && Array.isArray(response)) {
          // Create new array references to ensure React detects changes
          dispatch(setAllCources([...response]));
          setCources([...response]);
        }
      } catch (error) {
        console.error("hadleGetCources", error);
      }
    }
  };

  const getNextWeekdayDate = (weekday) => {
    const today = new Date();
    const daysOfWeek = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const targetDay = daysOfWeek[weekday];

    const todayDay = today.getDay();
    const weekDayNum = daysOfWeek[weekday];

    if (todayDay === weekDayNum) {
      return `Today & every ${weekday}`;
    } else {
      return `Every ${weekday} This week: ${format(
        nextDay(today, targetDay),
        "dd/MM/yyyy"
      )}`;
    }
  };

  const handleGetNote = async () => {
    setLoadingNote(true);
    try {
      const response = await getNotesByUser(userProfile?.id, userProfile?.role);
      const notesArray = Array.isArray(response) ? response : [];
      // Create new array reference to ensure React detects changes
      setNotes([...notesArray]);
    } catch (error) {
      console.error("handleGetNote", error);
      setNotes([]);
    } finally {
      setLoadingNote(false);
    }
  };

  const handleGetSessions = async () => {
    setIsLoading(true);
    try {
      const response = await getSessions(userProfile?.role, userProfile?.id);
      const sessionsArray = Array.isArray(response) ? response : [];
      let sortedSessons = sortMeetings(sessionsArray);
      // Create new array references to ensure React detects changes
      setSessions([...sortedSessons]);
      dispatch(setAllSessons([...sessionsArray]));
    } catch (error) {
      console.error("handleGetSessions", error);
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSessionEffectiveDate = (item, today) => {
    if (!item) return { effectiveDateStr: '', isToday: false };
    const todayStr = format(today, 'yyyy-MM-dd');
    const todayDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

    if (item.isRepeat) {
      if (!item.repeatDays) {
        return { effectiveDateStr: item.meetingDate || todayStr, isToday: false };
      }
      const daysOfWeek = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };
      const daysList = item.repeatDays.toLowerCase().split(',').map((d) => d.trim());
      const isRepeatToday = daysList.some((d) => daysOfWeek[d] === todayDay);
      if (isRepeatToday) {
        return { effectiveDateStr: todayStr, isToday: true };
      }
      let minDiff = 8;
      let targetDateStr = todayStr;
      for (const dayName of daysList) {
        const targetDayNum = daysOfWeek[dayName];
        if (targetDayNum !== undefined) {
          const diff = (targetDayNum - todayDay + 7) % 7 || 7;
          if (diff < minDiff) {
            minDiff = diff;
            const nextDate = new Date(today);
            nextDate.setDate(today.getDate() + diff);
            targetDateStr = format(nextDate, 'yyyy-MM-dd');
          }
        }
      }
      return { effectiveDateStr: targetDateStr, isToday: false };
    }

    const mDate = item.meetingDate ? item.meetingDate.split('T')[0] : '';
    const isToday = mDate === todayStr;
    return { effectiveDateStr: mDate, isToday };
  };

  const sortMeetings = (meetings) => {
    if (!Array.isArray(meetings)) return [];
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');

    const meetingsWithMeta = meetings.map((m) => {
      const { effectiveDateStr, isToday } = getSessionEffectiveDate(m, today);
      const timeStr = m.meetingTime || '00:00';
      return {
        ...m,
        _effectiveDate: effectiveDateStr,
        _isToday: isToday,
        _timeStr: timeStr,
      };
    });

    const todaySessions = [];
    const upcomingSessions = [];
    const pastSessions = [];

    for (const m of meetingsWithMeta) {
      if (m._isToday) {
        todaySessions.push(m);
      } else if (m._effectiveDate && m._effectiveDate > todayStr) {
        upcomingSessions.push(m);
      } else {
        pastSessions.push(m);
      }
    }

    // Today's sessions: sorted by time ascending (earliest to latest today)
    todaySessions.sort((a, b) => a._timeStr.localeCompare(b._timeStr));

    // Upcoming sessions: sorted by date ASC, then time ASC
    upcomingSessions.sort((a, b) => {
      const dateCmp = a._effectiveDate.localeCompare(b._effectiveDate);
      if (dateCmp !== 0) return dateCmp;
      return a._timeStr.localeCompare(b._timeStr);
    });

    // Past sessions: sorted by date DESC, then time DESC (most recently finished first)
    pastSessions.sort((a, b) => {
      const dateCmp = b._effectiveDate.localeCompare(a._effectiveDate);
      if (dateCmp !== 0) return dateCmp;
      return b._timeStr.localeCompare(a._timeStr);
    });

    return [...todaySessions, ...upcomingSessions, ...pastSessions];
  };

  const handleDeleteSession = async (id) => {
    setIsLoading(true);
    try {
      const response = await deleteSession(id);
      if (response && response.status === 200) {
        openNotification("success", "Success", "Session deleted successfully");
        await handleGetSessions();
      } else {
        openNotification("error", "Error", response?.data?.message);
      }
    } catch (error) {
      console.error("handleDeleteSession", error);
      openNotification("error", "Error", "Something went wrong");
    }
    setIsLoading(false);
  };

  const openNotification = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const isJoinLinkAccessible = (item) => {
    if (!item) return false;
    // Superadmin always has access
    if (userProfile?.role === "superadmin") return true;

    const now = new Date();

    // 1. Day check
    if (item.isRepeat) {
      if (!item.repeatDays) return false;
      const daysOfWeek = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      };
      const targetDayNum = daysOfWeek[String(item.repeatDays).toLowerCase()];
      if (now.getDay() !== targetDayNum) {
        return false;
      }
    } else {
      if (!item.meetingDate) return false;
      const todayStr = format(now, "yyyy-MM-dd");
      if (todayStr !== item.meetingDate) {
        return false;
      }
    }

    // 2. Time check
    if (!item.meetingTime) return false;
    const [startHour, startMinute] = item.meetingTime.split(':').map(Number);
    const startWindow = new Date(now);
    startWindow.setHours(startHour, startMinute, 0, 0);
    startWindow.setMinutes(startWindow.getMinutes() - 10);

    if (now < startWindow) {
      return false;
    }

    if (item.meetingEndTime) {
      const [endHour, endMinute] = item.meetingEndTime.split(':').map(Number);
      const endWindow = new Date(now);
      endWindow.setHours(endHour, endMinute, 0, 0);
      if (now > endWindow) {
        return false;
      }
    }

    return true;
  };

  const handleJoinMeeting = async (item) => {
    window.open(item?.meetingUrl, "_blank");

    if (item.meetingUrl) {
      await handleAttendance(item);
    }
  };

  const copyMeetingLink = async (item) => {
    if (item.meetingUrl) {
      await handleAttendance(item);
    }

    navigator.clipboard.writeText(item?.meetingUrl);
    messageApi.open({
      type: "success",
      content: "Meeting link copied to clipboard",
    });
  };

  const handleAttendance = async (item) => {
    if (userProfile?.role === "student" || userProfile?.role === "teacher") {
      try {
        const response = await attendToSession(
          item.id,
          userProfile?.id,
          item?.isRepeat
        );
        if (response && response.status === 200) {
          messageApi.open({
            type: "success",
            content: "Attendance marked successfully",
          });
          await handleGetSessions();
        }
      } catch (error) {
        console.error("handleAttendance", error);
      }
    }
  };

  const renderTags = (tags) => {
    return tags?.split(",").map((tag, index) => {
      if (tag === "VideoTutorials") {
        return (
          <Tag key={index} color="blue">
            Video Tutorials
          </Tag>
        );
      } else if (tag === "Exams") {
        return (
          <Tag key={index} color="blue">
            Exams
          </Tag>
        );
      } else if (tag === "TrainingAndCourses") {
        return (
          <Tag key={index} color="blue">
            Training and Courses
          </Tag>
        );
      } else if (tag === "News") {
        return (
          <Tag key={index} color="blue">
            News
          </Tag>
        );
      } else if (tag === "Seminar") {
        return (
          <Tag key={index} color="blue">
            Seminar
          </Tag>
        );
      }
      return null;
    });
  };

  const formatTimeDisplay = (time) => {
    if (!time) return "";
    try {
      const d = new Date(`1970-01-01T${time}`);
      if (isNaN(d.getTime())) return time;
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return time;
    }
  };

  const renderSessionList = (sessionList) => (
    <List
      itemLayout="horizontal"
      dataSource={sessionList}
      locale={{ emptyText: "No sessions available" }}
      renderItem={(item) => (
        <List.Item key={item.id}>
          <List.Item.Meta
            title={
              <div className="session-header">
                {isStaffSessionManager && (
                  <div className="actions">
                    <Button
                      type="link"
                      size="small"
                      className="edit-btn"
                      onClick={() => {
                        setSelectedSession(item);
                        setIsEditSessionOpen(true);
                      }}
                    >
                      <EditOutlined />
                    </Button>
                    {(userProfile?.role === "superadmin" || userProfile?.role === "admin") && (
                      <Popconfirm
                        title="Are you sure to delete this session?"
                        onConfirm={() => handleDeleteSession(item.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          danger
                          type="link"
                          size="small"
                          className="delete-btn"
                        >
                          <DeleteOutlined />
                        </Button>
                      </Popconfirm>
                    )}

                    {item?.isPublished ? (
                      <Tag color="green">Published</Tag>
                    ) : (
                      <Tag color="red">Not Published</Tag>
                    )}
                  </div>
                )}
              </div>
            }
            description={
              <div className="meeting-data">
                <div className="left">
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" }}>
                    {item?._isToday && (
                      <Tag color="gold" style={{ fontWeight: 600 }}>
                        🌟 Today's Session
                      </Tag>
                    )}
                    {item?.isFree ? (
                      <Tag color="green">Free</Tag>
                    ) : (
                      <Tag color="red">Paid</Tag>
                    )}
                    {item?.isRepeat ? (
                      <Tag color="purple">Repeats: {item?.repeatDays}</Tag>
                    ) : null}
                  </div>

                  <h2
                    className="meet-title"
                    style={{
                      width: isStaffSessionManager ? "70%" : "100%",
                    }}
                  >
                    {item?.name}
                  </h2>

                  <p className="meet-details">
                    <span>Course Name: </span>
                    {item?.courseName}
                  </p>
                  <div className="session-description-wrapper">
                    <ReactQuill
                      className="session-description"
                      readOnly
                      theme="bubble"
                      value={item?.description}
                    />
                  </div>

                  <Divider />
                  <div className="meeting-date-time">
                    {item?.meetingDate || item?.isRepeat ? (
                      <div className="date-continer">
                        <div>
                          <span>
                            <CalendarOutlined />
                          </span>
                          <div>
                            <p>
                              {item?.isRepeat
                                ? getNextWeekdayDate(item?.repeatDays)
                                : item?.meetingDate || "N/A"}
                            </p>
                          </div>
                        </div>
                        <div>
                          <span>
                            <ClockCircleOutlined />
                          </span>
                          <p className="id">
                            {formatTimeDisplay(item.meetingTime) || "N/A"}
                            {item.meetingEndTime && ` - ${formatTimeDisplay(item.meetingEndTime)}`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p>Meeting date will be available soon</p>
                    )}
                  </div>
                  <div className="meeting-link">
                    {item?.meetingUrl ? (
                      <div className="meetlink-continer">
                        <section className="links">
                          <Button
                            className="join-link"
                            type="default"
                            onClick={() => handleJoinMeeting(item)}
                            disabled={!isJoinLinkAccessible(item)}
                          >
                            Join Meeting
                          </Button>

                          {userProfile?.role === "student" &&
                            !item?.isRepeat && (
                              <div className="attendance">
                                {item.participation?.id ? (
                                  <span className="attended">
                                    You have attended the meeting
                                  </span>
                                ) : (
                                  <span className="not-attended">
                                    You have not attended yet
                                  </span>
                                )}
                              </div>
                            )}
                        </section>
                      </div>
                    ) : (
                      <div className="no-meet-links">
                        <p>Meeting link will be available soon</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="right">
                  <img src={getImageUrl(item?.courseCoverImage)} alt="course" />
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  return (
    <div className="home">
      {contextHolder}
      {contextHolderNoti}

      {/* Coordinator Stats Cards */}
      {userProfile?.role === "coordinator" && (
        <div style={{ marginBottom: '24px', padding: '0 24px' }}>
          <Spin spinning={statsLoading}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 10px 20px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '500', opacity: 0.95, color: '#fff' }}>Assigned Courses</h3>
                <p style={{ margin: '12px 0 0 0', fontSize: '36px', fontWeight: '700', lineHeight: 1 }}>{stats?.courseCount || 0}</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 10px 20px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '500', opacity: 0.95, color: '#fff' }}>Total Enrolled Users</h3>
                <p style={{ margin: '12px 0 0 0', fontSize: '36px', fontWeight: '700', lineHeight: 1 }}>{stats?.userCount || 0}</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)', color: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 10px 20px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '500', opacity: 0.95, color: '#fff' }}>Course Assessments</h3>
                <p style={{ margin: '12px 0 0 0', fontSize: '36px', fontWeight: '700', lineHeight: 1 }}>{stats?.assessmentCount || 0}</p>
              </div>
            </div>
          </Spin>
        </div>
      )}

      {/* KPI Stats Cards for Admins/Superadmins */}
      {(userProfile?.role === "superadmin" || userProfile?.role === "admin") && stats && (
        <div className="admin-home-stats">
          <div className="kpi-grid">
            <div className="kpi-card blue">
              <div className="kpi-icon-wrapper">
                <TeamOutlined />
              </div>
              <div className="kpi-content">
                <h3>Total Students</h3>
                <h2>{stats.studentsCount}</h2>
                <p>Enrolled learners</p>
              </div>
            </div>

            <div className="kpi-card gold">
              <div className="kpi-icon-wrapper">
                <BookOutlined />
              </div>
              <div className="kpi-content">
                <h3>Active Courses</h3>
                <h2>{stats.activeCoursesCount}</h2>
                <p>Published programs</p>
              </div>
            </div>

            <div className="kpi-card purple">
              <div className="kpi-icon-wrapper">
                <TeamOutlined />
              </div>
              <div className="kpi-content">
                <h3>Lecturers & Staff</h3>
                <h2>{stats.instructorsCount}</h2>
                <p>Eminent panel</p>
              </div>
            </div>

            <Link to="/dashboard/revenue" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="kpi-card green" style={{ cursor: 'pointer' }}>
                <div className="kpi-icon-wrapper">
                  <DollarOutlined />
                </div>
                <div className="kpi-content">
                  <h3>Total Revenue</h3>
                  <h2>{parseFloat(stats.totalRevenue).toLocaleString()} <span className="currency">LKR</span></h2>
                  <p>Confirmed payments • Click for details</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* KPI Stats Cards for Instructors */}
      {userProfile?.role === "instructor" && stats && (
        <div className="admin-home-stats">
          <div className="kpi-grid">
            <div className="kpi-card blue">
              <div className="kpi-icon-wrapper">
                <BookOutlined />
              </div>
              <div className="kpi-content">
                <h3>Conducted Courses</h3>
                <h2>{stats.instructorCoursesCount}</h2>
                <p>Your assigned programs</p>
              </div>
            </div>

            <div className="kpi-card gold">
              <div className="kpi-icon-wrapper">
                <TeamOutlined />
              </div>
              <div className="kpi-content">
                <h3>Total Learners</h3>
                <h2>{stats.instructorStudentsCount}</h2>
                <p>Enrolled students</p>
              </div>
            </div>

            <div className="kpi-card purple">
              <div className="kpi-icon-wrapper">
                <CalendarOutlined />
              </div>
              <div className="kpi-content">
                <h3>Active Sessions</h3>
                <h2>{stats.instructorSessionsCount}</h2>
                <p>Scheduled interactive classes</p>
              </div>
            </div>

            <div className="kpi-card green">
              <div className="kpi-icon-wrapper">
                <VideoCameraOutlined />
              </div>
              <div className="kpi-content">
                <h3>Course Materials</h3>
                <h2>{stats.instructorMaterialsCount}</h2>
                <p>Uploaded video & notes</p>
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="home-continer">
        <div className="home-left">
          <div className="updates-body">
            <div className="sessions-header">
              <h1>Your Sessions Timeline</h1>
              {(userProfile?.role === "superadmin" || userProfile?.role === "admin" || userProfile?.role === "coordinator") && (
                <Button
                  onClick={() => setIsAddSessionOpen(true)}
                  type="primary"
                  className="add-btn"
                >
                  <PlusOutlined /> Add Session
                </Button>
              )}
            </div>
            <Spin spinning={isLoading} tip="Please wait...">
              {isStaffSessionManager ? (
                <Tabs
                  defaultActiveKey="published"
                  items={[
                    {
                      key: "published",
                      label: `Published (${publishedSessions.length})`,
                      children: renderSessionList(publishedSessions),
                    },
                    {
                      key: "not_published",
                      label: `Not Published (${unpublishedSessions.length})`,
                      children: renderSessionList(unpublishedSessions),
                    },
                  ]}
                />
              ) : (
                renderSessionList(publishedSessions)
              )}
            </Spin>
          </div>
        </div>

        <div className="home-right">
          <div className="updates-body">
            <div className="sessions-header">
              <h1>Special Notes</h1>
            </div>

            <Spin spinning={loadingNote} tip="Please wait...">
              <div className="special-notes">
                <div className="notes-body">
                  <List
                    className="notes-list"
                    itemLayout="horizontal"
                    dataSource={notes}
                    renderItem={(item) => (
                      <List.Item key={item.id} className="note-description">
                        <h3>
                          <span>Course: </span>
                          {item?.courseName}
                        </h3>
                        <div className="note-quill">
                          <ReactQuill
                            readOnly
                            theme="bubble"
                            value={item?.note}
                          />
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
              </div>
            </Spin>
          </div>
        </div>
      </div>

      {filteredHomeCourses?.length > 0 && userProfile?.role !== "instructor" && (
        <div className="home-courses-dashboard">
          <h2 className="title">Featured Courses</h2>
          <div className="courses-body">
            <List
              grid={{
                gutter: 30,
                xs: 1,
                sm: 1,
                md: 1,
                lg: 2,
                xl: 3,
                xxl: 4,
              }}
              dataSource={filteredHomeCourses}
              renderItem={(item) => (
                <List.Item key={item.id}>
                  <div className="course-card">
                    <div className="card-image-container">
                      <img src={getImageUrl(item?.coverImage)} alt="" />
                      <div className="cource-card-header-buttons">
                        <div
                          className="duration"
                          style={{
                            backgroundColor: item?.isPublished
                              ? "#4CAF50"
                              : "#f44336",
                          }}
                        >
                          <p>
                            {item?.isPublished ? "Published" : "Not Published"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="card-description">
                        <div className="price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', width: '100%' }}>
                          {item?.price === 0 ? (
                            <span className="free-text" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#52c41a' }}>Free</span>
                          ) : (
                            <>
                              {item?.isPromoActive && item?.promoStartDate && item?.promoEndDate && new Date() >= new Date(item.promoStartDate) && new Date() <= new Date(item.promoEndDate) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ color: '#ff4d4f', fontSize: '1.25rem', fontWeight: '700', whiteSpace: 'nowrap' }}>{item?.promoPrice} LKR</span>
                                    <span style={{ textDecoration: 'line-through', color: '#8c8c8c', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{item?.price} LKR</span>
                                  </div>
                                  <PromoCountdown endDate={item.promoEndDate} />
                                </div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f1f1f', whiteSpace: 'nowrap' }}>{item?.price} LKR</span>
                                  {item?.alumniDiscount > 0 && item?.prerequisiteCourseIds && (() => { try { const ids = typeof item.prerequisiteCourseIds === 'string' ? JSON.parse(item.prerequisiteCourseIds) : item.prerequisiteCourseIds; return Array.isArray(ids) && ids.length > 0; } catch { return false; } })() && (
                                    <span style={{ color: '#52c41a', fontSize: '0.85rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                      Alumni Price: {item.price - item.alumniDiscount} LKR
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="author">
                          <img
                            src={
                              item?.createdBy?.profileImage
                                ? getImageUrl(item?.createdBy?.profileImage)
                                : DEFAULT_USER_PROFILE_IMAGE
                            }
                            alt=""
                          />
                          <h3>{item?.createdBy?.name}</h3>
                        </div>
                      </div>

                      <h3 className="title">{item?.name}</h3>

                      {item?.duration && (
                        <div style={{ marginBottom: "10px", fontSize: "13px", fontWeight: "600", color: "#666" }}>
                          Duration: {item.duration}
                        </div>
                      )}

                      <div className="tags">{renderTags(item?.tags)}</div>
                    </div>

                    <div className="card-footer">
                      <div className="button-continer">
                        <Link
                          className="enroll-now"
                          to={`/dashboard/cources/${item.id}`}
                        >
                          <Button type="link">View</Button>
                        </Link>
                        <>
                          {item?.whatsappGroupLink && (
                            <a
                              href={item?.whatsappGroupLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button
                                type="primary"
                                style={{
                                  backgroundColor: "#25d366",
                                }}
                              >
                                <WhatsAppOutlined />
                                Join Whatsapp Group
                              </Button>
                            </a>
                          )}
                        </>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </div>
      )}

      <AddSession
        isOpen={isAddSessionOpen}
        setOpen={setIsAddSessionOpen}
        cources={cources}
        handleGetSessions={handleGetSessions}
      />

      <EditSession
        isOpen={isEditSessionOpen}
        setOpen={setIsEditSessionOpen}
        selectedSession={selectedSession}
        cources={cources}
        handleGetSessions={handleGetSessions}
      />
    </div>
  );
};

export default Home;
